import {ChevronDownIcon, ChevronRightIcon, FileSymlinkFileIcon} from '@primer/octicons-react'
import {IconButton, Label, Link, Heading} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import styles from './ConversationHeader.module.css'
import {useReviewThreadCollapseState} from '@github-ui/use-review-thread-collapse-state'

type ConversationHeaderProps = {
  firstCommentId?: number | null
  isCollapsed: boolean
  isOutdated: boolean
  isResolved: boolean
  startLine?: number | null
  line?: number | null
  onNavigateToDiffComment: () => void
  onToggleCollapsed: () => void
  path: string
  rightSideContent?: React.ReactElement | null
  threadId: string
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export function ConversationHeader({
  firstCommentId,
  isCollapsed: initialIsCollapsed,
  isOutdated,
  isResolved,
  startLine,
  line,
  onToggleCollapsed,
  onNavigateToDiffComment,
  path,
  rightSideContent,
  threadId,
  headingLevel = 'h3',
}: ConversationHeaderProps) {
  const [isCollapsed, setIsCollapsed] = useReviewThreadCollapseState(threadId, initialIsCollapsed)

  const lineText = startLine ? `Lines ${startLine} - ${line}` : `Line ${line}`
  const handleToggleCollapsed = () => {
    setIsCollapsed(prevIsCollapsed => !prevIsCollapsed)
    onToggleCollapsed()
  }

  return (
    <div className="d-flex flex-row flex-items-center">
      <IconButton
        aria-label={isCollapsed ? 'Open review comment' : 'Close review comment'}
        icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
        size="small"
        variant="invisible"
        onClick={handleToggleCollapsed}
        className="ml-n1"
      />
      <Heading as={headingLevel} className="d-flex flex-items-center flex-1 min-width-0 mr-2 ml-1 f6 text-semibold">
        <Tooltip direction="n" text={path} type="label">
          <Link
            className={clsx(
              styles['file-name-overflow'],
              'd-inline text-mono no-wrap overflow-hidden direction-rtl fgColor-default',
            )}
            onClick={onNavigateToDiffComment}
            href={`#r${firstCommentId}`}
            muted
          >
            {/* Ensures path is displayed in left-to-right-order despite container direction being right-to-left */}
            &lrm;{path}&lrm;
          </Link>
        </Tooltip>
        {!!line && <span className="f6 fgColor-muted text-normal no-wrap ml-2">{lineText}</span>}
      </Heading>
      {isResolved && (
        <Label size="large" className="mx-1" variant="secondary">
          Resolved
        </Label>
      )}
      {isOutdated && !isResolved && (
        <Label size="large" className="mx-1" variant="attention">
          Outdated
        </Label>
      )}
      <IconButton
        as="a"
        aria-label="Jump to the comment in the diff"
        tooltipDirection="se"
        icon={FileSymlinkFileIcon}
        variant="invisible"
        href={`#r${firstCommentId}`}
        onClick={onNavigateToDiffComment}
      />
      {rightSideContent}
    </div>
  )
}
